import React, { useState, useEffect, useRef } from "react";
import useModalDialog from "../../hooks/useModalDialog";
import "../../styles/WelcomeTutorial.css";

const TUTORIAL_QUERIES = [
  "Cost of a new garden fence?",
  "Price to rewire a 3-bed house?",
  "Quote for a loft conversion?",
];

const WelcomeTutorial = ({ onClose, onFinish }) => {
  const dialogRef = useRef(null);
  useModalDialog(dialogRef, true, onClose);
  const [currentStep, setCurrentStep] = useState(1);
 const steps = 5;

  // --- Animation State Refs ---
  const textElRef = useRef(null);
  const caretElRef = useRef(null);
  const chatBubblesRef = useRef([]);
  const contractorCardsRef = useRef([]);
  useEffect(() => {
    if (currentStep === 5) dialogRef.current?.querySelector("#finish-btn")?.focus();
    else if (document.activeElement?.hidden) dialogRef.current?.querySelector(".next-link")?.focus();
  }, [currentStep]);

  // --- Effect for Step 1: Typing Animation ---
  useEffect(() => {
    if (currentStep !== 1) return;
    let queryIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typeTimeout;

    const type = () => {
      const textEl = textElRef.current;
      const caretEl = caretElRef.current;
      if (!textEl || !caretEl) return;

      const currentQuery = TUTORIAL_QUERIES[queryIndex];
      caretEl.style.display = "inline-block";

      if (isDeleting) {
        textEl.textContent = currentQuery.substring(0, charIndex - 1);
        charIndex--;
        if (charIndex === 0) {
          isDeleting = false;
          queryIndex = (queryIndex + 1) % TUTORIAL_QUERIES.length;
        }
        typeTimeout = setTimeout(type, 50);
      } else {
        textEl.textContent = currentQuery.substring(0, charIndex + 1);
        charIndex++;
        if (charIndex === currentQuery.length) {
          isDeleting = true;
          typeTimeout = setTimeout(type, 2000);
        } else {
          typeTimeout = setTimeout(type, 100);
        }
      }
    };
    typeTimeout = setTimeout(type, 500);
    return () => clearTimeout(typeTimeout);
  }, [currentStep]);

  // --- Effect for Step 2: Chat Bubbles ---
  useEffect(() => {
    if (currentStep !== 2) return; 
    const bubbles = chatBubblesRef.current;
    bubbles.forEach(b => b?.classList.remove('visible'));
    const timeouts = [
      setTimeout(() => bubbles[0]?.classList.add("visible"), 200),
      setTimeout(() => bubbles[1]?.classList.add("visible"), 700),
      setTimeout(() => bubbles[2]?.classList.add("visible"), 1200),
      setTimeout(() => bubbles[3]?.classList.add("visible"), 1700),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, [currentStep]);

  // --- Effect for Step 3: Review Summary (Price box) ---
  useEffect(() => {
    if (currentStep !== 3) return;
    const cards = contractorCardsRef.current;
    cards.forEach((card) => card.classList.remove("active"));
  }, [currentStep]);

  // --- Effect for Step 4: Contractor Cards ---
  useEffect(() => {
    if (currentStep !== 4) return;
    const cards = contractorCardsRef.current;
    if (cards.length === 0) return;
    const bubbles = chatBubblesRef.current;
    bubbles.forEach(b => b?.classList.remove('visible'));
    
    let cardIndex = 0;
    const cycle = () => {
      cards.forEach((card) => card.classList.remove("active"));
      cardIndex = (cardIndex + 1) % cards.length;
      cards[cardIndex]?.classList.add("active");
    };
    
    cards[0].classList.add("active");
    const interval = setInterval(cycle, 2000);
    
    return () => clearInterval(interval);
  }, [currentStep]);

  // Reading speed varies: only the visitor advances the instructions.
  // --- Navigation ---
  const nextStep = () => {
    if (currentStep < 5) {
    setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
    setCurrentStep(currentStep - 1); // <-- FIX: This was currentStep + 1
    }
  };

  const handleFinish = () => {
    onFinish();
  };

  return (
    <div id="modal-overlay" className="visible">
      <div ref={dialogRef} id="welcome-modal" role="dialog" aria-modal="true" aria-label="Welcome to Pricing Penguin" tabIndex={-1}>
        <div id="modal-skip-container">
          <button type="button" className="skip-link-top" onClick={onClose}>
            Skip
          </button>
        </div>

        <div id="modal-step-dots">
          {[...Array(steps)].map((_, i) => (
            <span
              key={i}
              className={`step-dot ${i + 1 === currentStep ? "active" : ""}`}
            ></span>
          ))}
        </div>

        {/* --- Step 1: Describe Project --- */}
        <div hidden={currentStep !== 1} className={`modal-step ${currentStep === 1 ? "active" : ""}`}>
          <h2 className="modal-title">1. Describe Your Project</h2>
          <p className="modal-description">
            Start a chat and tell our AI what you need, just like in a normal
            conversation.
          </p>
          <div className="animation-container">
            <div id="fake-input-box">
              <span ref={textElRef} id="fake-input-text"></span>
              <span ref={caretElRef} id="fake-input-caret"></span>
            </div>
          </div>
        </div>

        {/* --- Step 2: Get Instant Estimates --- */}
        <div hidden={currentStep !== 2} className={`modal-step ${currentStep === 2 ? "active" : ""}`}>
          <h2 className="modal-title">2. Get Instant Estimates</h2>
          <p className="modal-description">
            Our AI will ask a few simple questions to understand the job and
            provide a free, data-driven price estimate.
          </p>
          <div className="animation-container">
            <div className="chat-bubble-container">
              <div
                ref={(el) => (chatBubblesRef.current[0] = el)}
                className="chat-bubble bot"
              >
                Hi! How can I help today?
              </div>
              <div
                ref={(el) => (chatBubblesRef.current[1] = el)}
                className="chat-bubble user"
              >
                I'd like a price to re-plaster my living room.
              </div>
              <div
                ref={(el) => (chatBubblesRef.current[2] = el)}
                className="chat-bubble bot"
              >
                Sure! What are the dimensions (L x W)?
              </div>
              <div
                ref={(el) => (chatBubblesRef.current[3] = el)}
                className="chat-bubble user"
              >
                It's about 5m by 4m.
              </div>
            </div>
          </div>
        </div>

        {/* --- Step 3: Review Your Summary --- */}
        <div hidden={currentStep !== 3} className={`modal-step ${currentStep === 3 ? "active" : ""}`}>
          <h2 className="modal-title">3. Review Your Project Summary</h2>
          <p className="modal-description">
            Once you have your estimate, the AI will create a final summary.
            Click the button to see it!
          </p>
          <div className="animation-container">
            <div className="price-range-box">
              <p>Estimated Price Range</p>
              <p className="price-text">£650 - £800</p>
            </div>
            <button
              className="button button-secondary tutorial-summary-btn"
              disabled
            >
              Go to Project Summary
            </button>
          </div>
        </div>

        {/* --- Step 4: Connect With Local Pros --- */}
        <div hidden={currentStep !== 4} className={`modal-step ${currentStep === 4 ? "active" : ""}`}>
          <h2 className="modal-title">4. Connect With Local Pros</h2>
          <p className="modal-description">
            We'll show you a list of trusted, available contractors in your
            area. Just pick your favorite!
          </p>
          <div id="contractor-card-container">
            {[
              { name: "ProBuild", logo: "PB", location: "London, SW1A", rating: 5, reviews: 12 },
              { name: "Ace Decor", logo: "AD", location: "Manchester, M1", rating: 4.5, reviews: 8 },
              { name: "Smith & Sons", logo: "S&S", location: "Bristol, BS1", rating: 4.8, reviews: 21 },
            ].map((contractor, index) => (
              <div
                key={contractor.name}
                ref={(el) => (contractorCardsRef.current[index] = el)}
                className="contractor-card"
              >
                <div className="contractor-card-header">
                  <img
                    src={`https://placehold.co/40x40/e2e8f0/64748b?text=${contractor.logo}`}
                    alt="Contractor Logo"
                    className="contractor-logo"
                    loading="lazy"
                  />
                  <div className="contractor-card-title">
                    <span className="contractor-name">{contractor.name}</span>
                    <span className="contractor-location">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M7.125 1.661a.75.75 0 0 1 1.75 0c.322.54.814 1.298 1.357 2.135.539.831 1.11 1.719 1.638 2.593.535.882.99 1.782 1.31 2.614.288.752.42 1.48.42 2.122 0 3.038-2.343 5.5-5.25 5.5S1.75 14.156 1.75 11.125c0-.642.132-1.37.42-2.122.32-.832.775-1.732 1.31-2.614.528-.874 1.1-1.762 1.638-2.593.543-.837 1.035-1.594 1.357-2.135Z" clipRule="evenodd" /></svg>
                      {contractor.location}
                    </span>
                  </div>
                </div>
                <div className="contractor-card-rating">
                  <span className="stars">★★★★☆</span>
                  <span className="review-count">({contractor.reviews} reviews)</span>
                </div>
                <div className="contractor-card-actions">
                  <button className="btn-details" disabled>Show Details</button>
                  <button className="btn-select" disabled>Select</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- Step 5: You're All Set! --- */}
        <div hidden={currentStep !== 5} className={`modal-step ${currentStep === 5 ? "active" : ""}`}>
          <h2 className="modal-title">You're ready to go!</h2>
          <div className="animation-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-20 h-20 text-blue-500"
            >
              <path
                fillRule="evenodd"
                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                clipRule="evenodd"
              />
            </svg>
            <p className="modal-description" style={{ marginTop: "1rem" }}>
              It's that simple. Start your first chat to get a free estimate
              today.
            </p>
          </div>
          <button
            id="finish-btn"
            className="button button-primary"
            onClick={handleFinish}
          >
            Start Your First Chat
          </button>
        </div>

        {/* --- Bottom Controls --- */}
        <div id="modal-bottom-controls">
          <button
            type="button"
            hidden={currentStep === 1}
            className={`back-link ${currentStep > 1 ? "visible" : ""}`}
            onClick={prevStep}
          >
            Back
          </button>
          <div id="modal-step-counter" role="status">{`Step ${currentStep} of ${steps}`}</div>
          <button
            type="button"
            hidden={currentStep === 5}
            className={`next-link ${currentStep < 5 ? "visible" : ""}`}
            onClick={nextStep}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTutorial;
